// src/domains/me.ts —— H5 我的 / 返佣 / 消息中心 / 安全设置 / KYC / 客服 / 语言（原生；06 U-09～U-15、U-19）
// 不读写原型假数据键（kycStatus kycForm msgTab commAvail twoFA tgBound pwdForm devices faqOpen tickets），本域状态全部在切片 m
import { ApiError } from '../../../shared/src/api/client';
import type { CardProduct, Profile, PublicConfig, TokenResp, UserPage } from '../../../shared/src/api/types';
import { fmtAmount, fmtCommission, fmtPlain, toMicro } from '../../../shared/src/money';
import { CursorList } from '../../../shared/src/page';
import { createPoller, type Poller } from '../../../shared/src/poll';
import { qrDataUrl } from '../../../shared/src/qr';
import { getTg } from '../../../shared/src/tg';
import { fmtDateTime, tzOffsetLabel } from '../../../shared/src/time';
import { I18N_ME, REWRITES_ME } from '../i18n/me';
import type { Screen } from '../state';
import { cardCommonGroup, EXAMPLE_DEPOSIT, productGroups, walletGroup, type FeeQuote } from './fees';
import { GATE_MODAL, gateMissing, type GateMissing } from './gate';
import { listFlags } from './listview';
import type { UserWallet } from './wallet.types';
import { KEYS, MODAL_KEYS } from './me.keys';
import type {
  CommissionRow, Device, EmailFlow, FaqResp, KycDocType, KycForm, KycPick, KycView, MeSlice, MergeFlow, NoticeRow, NotificationRow, PwdForm, Referral,
  TfaFlow, TfaSetup, TicketDetail, TicketRow,
} from './me.types';
import { botIdField, str, type H5Ctx, type H5Domain, type InputEvent } from './types';

declare module '../state' {
  interface H5Slices {
    m: MeSlice;
  }
}

type Dict = Readonly<Record<string, unknown>>;

const PWD_RE = /^(?=.*[A-Za-z])(?=.*\d).{8,64}$/; // X-24
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i; // 同原型 L792 / U-01
const CODE_LEN = 6;
const CD_SECONDS = 60;
const PAGE_SIZE = 20; // U-22 E-04
const KYC_POLL_MS = 30_000; // U-22 E-05
// 07 §7.3 / X-36：单张 ≤ 8MiB（= 后端 kyc.MaxFileBytes）；三张上限合计 24MiB，加文本字段仍在后端 kycMaxBody（3×8MiB+64KiB ≈ 24.1MiB）
// 与 nginx client_max_body_size 30m 之内，故逐张预检即覆盖整体上限，超限请求不会发出
const KYC_MAX_BYTES = 8 * 1024 * 1024;
const KYC_MIME: readonly string[] = ['image/jpeg', 'image/png', 'image/webp'];
// multipart 文件段名（backend/internal/httpapi/routes_kyc.go kycFileFields）：顺序 = kycSlots 正面 / 反面 / 手持
const KYC_FACES = ['front', 'back', 'selfie'] as const;
const KYC_FACE_SET: ReadonlySet<string> = new Set<string>(KYC_FACES);
const TICKET_TITLE_MAX = 100; // 06 U-12 输入字段
const TICKET_CONTENT_MAX = 2000;
const KYC_STATES = ['none', 'pending', 'passed', 'rejected'] as const;
const TFA_QR_SIZE = 168; // 0453b 片 img CSS 定宽 168px

// 二维码按设备像素比生成位图（CSS 定宽不变），上限 3 倍控制 data URL 体积
function qrPixels(css: number): number {
  const dpr = typeof window !== 'undefined' && typeof window.devicePixelRatio === 'number' && Number.isFinite(window.devicePixelRatio) ? window.devicePixelRatio : 1;
  return css * Math.min(3, Math.max(1, Math.ceil(dpr)));
}
// 模板直接展示的本域新增文案键（need2faTip 属 shell errors 字典）
export const ME_TEXT_KEYS = [
  'rechargeCommissionBase', 'markAllRead', 'secEmail', 'secMerge', 'secMergeSub', 'secRevokeOthers', 'secNeedEmail', 'tfaScan', 'tfaDisableSub',
  'secMergeWarn', 'need2faTip', 'ticketTitleLabel', 'ticketContentLabel', 'tgBindHint', 'openBot',
  // U-23 费率说明页
  'feesTitle', 'feesNote', 'feesLoading', 'feesFailed', 'feesNoProducts',
  // U-24 资金动作拦截引导弹层
  'gateMergeLink', 'gateLater',
] as const;
type KycState = (typeof KYC_STATES)[number];

function fresh(): MeSlice {
  return {
    referral: null, comms: null, msgTab: 0, notifs: null, devices: [],
    pwd: { old: '', nw: '', nw2: '', revoke: true, code: '', cd: 0 },
    tfa: null, emailFlow: null, merge: null, faqOpen: -1, faqs: null,
    faqFailed: false,
    kycForm: { type: 'passport', name: '', idno: '', country: '' }, kycPicks: [null, null, null], kyc: null, kycThumbs: {}, kycPreview: '',
    ticket: { title: '', content: '' }, notices: null, tickets: null, ticketDetail: null, replyText: '',
    fees: { products: null, quote: null, depRate: null, loading: false, failed: false }, rev: 0,
  };
}

// 模块级资源：倒计时与 KYC 轮询（会话切换 / 卸载时统一释放）
let emailCdTimer: ReturnType<typeof setInterval> | null = null;
let pwdCdTimer: ReturnType<typeof setInterval> | null = null;
let kycPoller: Poller | null = null;
let unForeground: (() => void) | null = null;
// 会话代号：session:ready / lost 递增；await 之后代号已变（换人 / 退出）的结果一律丢弃，不写切片、不 toast
let gen = 0;
// KYC 页进入代号：离页 / 换会话递增；旧轮询应答与旧取图结果丢弃（取图结果随即 revoke）
let kycVisit = 0;
const thumbInflight = new Set<string>(); // `${kycVisit}:${文件 id}`：同一次进入内同一文件只取一次

function cur(ctx: H5Ctx): MeSlice {
  return ctx.host.state.m;
}

function setM(ctx: H5Ctx, patch: Partial<MeSlice>): void {
  ctx.host.setState({ m: { ...ctx.host.state.m, ...patch } });
}

function bump(ctx: H5Ctx): void {
  setM(ctx, { rev: cur(ctx).rev + 1 });
}

function profileOf(ctx: H5Ctx): Profile | null {
  return ctx.host.state.sh.profile;
}

function dict(ctx: H5Ctx): Dict {
  return ctx.gp.ui.dict();
}

function fill(text: string, vars: Readonly<Record<string, unknown>>): string {
  return text.replace(/\{(\w+)\}/g, (m, k: string) => {
    const v = vars[k];
    return v === undefined || v === null ? m : String(v);
  });
}

// 佣金「{金额} USDT」：去尾零 + 千分位（对客评审 2026-10-15 #26：佣金单位统一 USDT，不加 $）；空值按 0，非法值记日志后显示 —
function commText(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return fmtCommission('0');
  return fmtCommission(String(v), { fallback: '—' });
}

// 服务端六位小数字符串 → 两位截断 + 千分位（D8-12）；非法值记日志不抛（渲染期不得崩）
function money2(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '0.00';
  try {
    return fmtAmount(String(v), 2);
  } catch (e) {
    console.warn('[gp:me] 金额格式非法', v, e);
    return '—';
  }
}

function positive(v: string | number | null | undefined): boolean {
  if (v === null || v === undefined || v === '') return false;
  try {
    return toMicro(String(v)) > 0n;
  } catch {
    return false;
  }
}

// amount / credited_gross 均为合法金额且数值不同 → true；任一缺失或非法 → false（按无折算渲染，不抛）
function fxAdjusted(amount: unknown, gross: unknown): boolean {
  if (typeof amount !== 'string' || typeof gross !== 'string' || gross === '') return false;
  try {
    return toMicro(amount) !== toMicro(gross);
  } catch {
    return false;
  }
}

// 限额展示（D11 kycWhy / kycPassTip）：整数省略小数位（"2000.000000" → "2,000"），否则两位
function limitText(v: string | number | null | undefined): string {
  const s = money2(v);
  return s.endsWith('.00') ? s.slice(0, -3) : s;
}

// 返佣率（config.commission.l1_top 等）：十进制串去尾零（"10.000000" → "10"）
function pct(v: string | number | undefined): string {
  if (v === undefined || v === null) return '0';
  const s = String(v);
  return /^\d+(\.\d+)?$/.test(s) ? s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '') : s;
}

// 同原型 L784 rsTargetMask：前 2 *** 后 3
function maskTarget(v: string): string {
  return v.replace(/^(.{2}).*(.{3})$/, '$1***$2');
}

function digits(v: string, n = CODE_LEN): string {
  return v.replace(/\D/g, '').slice(0, n);
}

function kycStateOf(ctx: H5Ctx): KycState {
  const raw = cur(ctx).kyc?.status ?? ctx.host.state.sh.profile?.kyc_status ?? 'none';
  return KYC_STATES.find((k) => k === raw) ?? 'none';
}

function badgeText(n: number): string {
  return n > 99 ? '99+' : String(n);
}

// ---------- U-12 通知模板（tpl = 基线 §11.5；params 键 = 06 U-12 渲染契约） ----------
// 跳转目标（06 U-12 跳转列）：屏 / card = 按 params.card_id 进卡详情（U-05）/ ledgerDeposit、ledgerFee = U-07 指定页签
type NtGo = Screen | 'card' | 'ledgerDeposit' | 'ledgerFee';

interface NtSpec {
  title: string;
  body: string;
  failTitle?: string;
  failBody?: string;
  go: NtGo;
  failGo?: NtGo; // params.result=failed 时的跳转（card_closed：成功 → U-02a，失败 → U-05）
}

const NT: Readonly<Record<string, NtSpec>> = {
  deposit_credited: { title: 'ntDepositTitle', body: 'ntDepositBody', go: 'ledgerDeposit' },
  card_opened: { title: 'ntCardOpenedTitle', body: 'ntCardOpenedBody', go: 'card' },
  card_open_failed: { title: 'ntCardOpenFailedTitle', body: 'ntCardOpenFailedBody', go: 'myCards' },
  sms_code: { title: 'ntSmsTitle', body: 'ntSmsBody', go: 'card' },
  kyc_passed: { title: 'ntKycPassTitle', body: 'ntKycPassBody', go: 'kyc' },
  kyc_rejected: { title: 'ntKycRejectTitle', body: 'ntKycRejectBody', go: 'kyc' },
  withdrawal_paid: { title: 'ntWdPaidTitle', body: 'ntWdPaidBody', go: 'withdrawUsdt' },
  withdrawal_rejected: { title: 'ntWdRejectTitle', body: 'ntWdRejectBody', go: 'withdrawUsdt' },
  withdrawal_refunded: { title: 'ntWdRefundTitle', body: 'ntWdRefundBody', go: 'withdrawUsdt' },
  commission_settled: { title: 'ntCommTitle', body: 'ntCommBody', go: 'referral' },
  card_recharged: { title: 'ntRechargeTitle', body: 'ntRechargeBody', failTitle: 'ntRechargeFailTitle', failBody: 'ntRechargeFailBody', go: 'card' },
  card_withdrawn: { title: 'ntCardWdTitle', body: 'ntCardWdBody', failTitle: 'ntCardWdFailTitle', failBody: 'ntCardWdFailBody', go: 'card' },
  card_closed: { title: 'ntClosedTitle', body: 'ntClosedBody', failTitle: 'ntCloseFailTitle', failBody: 'ntCloseFailBody', go: 'myCards', failGo: 'card' },
  tx_declined: { title: 'ntDeclinedTitle', body: 'ntDeclinedBody', go: 'card' },
  tx_dispute: { title: 'ntDisputeTitle', body: 'ntDisputeBody', go: 'card' },
  fee_charged: { title: 'ntFeeTitle', body: 'ntFeeBody', go: 'ledgerFee' },
  card_tx_authorized: { title: 'ntTxAuthTitle', body: 'ntTxAuthBody', go: 'card' }, // P13 卡交易推送（2026-10-15）
  card_tx_settled: { title: 'ntTxSettleTitle', body: 'ntTxSettleBody', go: 'card' },
  card_tx_reversed: { title: 'ntTxReverseTitle', body: 'ntTxReverseBody', go: 'card' },
  card_tx_refunded: { title: 'ntTxRefundTitle', body: 'ntTxRefundBody', go: 'card' },
  account_merged: { title: 'ntMergedTitle', body: 'ntMergedBody', go: 'security' },
};

const CARD_TX_TPL: ReadonlySet<string> = new Set(['card_tx_authorized', 'card_tx_settled', 'card_tx_reversed', 'card_tx_refunded']);

// card_open_failed.reason_key → 文案（C-19 / TD-26；未收录按 unknown）
const REASON_KEY: Readonly<Record<string, string>> = {
  budget_unavailable: 'rsBudgetUnavailable', upstream_rejected: 'rsIssuerDeclined', upstream_timeout: 'rsNotConfirmed', card_limit: 'rsCardLimit', unknown: 'rsInternal',
};

// 体验评审 2026-10-15 #3：服务端站内通知参数保留 6 位小数原值，渲染前按键格式化，不把「100.123000」「$100.000000」原样给用户。
// 余额 / 卡额度 / 卡侧金额：两位截断 + 千分位（D8-12）；USDT 流水金额（手续费、到账、退回等）：按真实位数去掉全部尾零（用户拍板 2026-10-15：0.105 / 0 / 1.5，与 Bot / 邮件同口径）
const NT_TWO_DP: ReadonlySet<string> = new Set(['balance', 'available', 'limit']);
const NT_EXACT: ReadonlySet<string> = new Set(['fee', 'close_fee', 'net_amount', 'net', 'refund_total', 'refund', 'paid_amount', 'owed', 'owed_settled', 'activation_refund', 'credited_gross']);
// amount 键语义随模板而异：钱包充值 / 提现为 USDT 链上金额（保留尾数位），其余（卡额度、卡交易）为两位；佣金见 NT_COMMISSION_TPL
const NT_EXACT_AMOUNT_TPL: ReadonlySet<string> = new Set(['deposit_credited', 'withdrawal_paid', 'withdrawal_rejected', 'withdrawal_refunded']);

function ntAmount(v: unknown, exact: boolean): unknown {
  if (typeof v !== 'string' && typeof v !== 'number') return v;
  try {
    const m = toMicro(String(v));
    if (!exact) return fmtAmount(m, 2);
    const [int = '0', frac = ''] = fmtAmount(m, 6).split('.');
    const f = frac.replace(/0+$/, '');
    return f ? `${int}.${f}` : int;
  } catch (e) {
    console.warn('[gp:me] 通知金额格式非法，按原值展示', v, e);
    return v;
  }
}

// 佣金类模板：金额键一律去尾零（对客评审 2026-10-15 #26，与 Bot P8 / 邮件 M-14 同口径「{金额} USDT」）
const NT_COMMISSION_TPL: ReadonlySet<string> = new Set(['commission_settled']);

function ntPlain(v: unknown): unknown {
  if (typeof v !== 'string' && typeof v !== 'number') return v;
  try {
    return fmtPlain(String(v));
  } catch (e) {
    console.warn('[gp:me] 通知佣金金额格式非法，按原值展示', v, e);
    return v;
  }
}

function ntVars(tpl: string, p: Readonly<Record<string, unknown>>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...p };
  for (const k of Object.keys(out)) {
    if (NT_COMMISSION_TPL.has(tpl) && (k === 'amount' || k === 'balance')) out[k] = ntPlain(out[k]);
    else if (NT_TWO_DP.has(k)) out[k] = ntAmount(out[k], false);
    else if (NT_EXACT.has(k)) out[k] = ntAmount(out[k], true);
    else if (k === 'amount') out[k] = ntAmount(out[k], NT_EXACT_AMOUNT_TPL.has(tpl));
  }
  return out;
}

export function renderNotification(t: Dict, n: NotificationRow): { title: string; body: string } {
  const spec = NT[n.tpl];
  if (!spec) return { title: str(t, 'ntUnknownTitle'), body: '' };
  const p: Readonly<Record<string, unknown>> = n.params ?? {};
  const failed = p.result === 'failed' && spec.failTitle !== undefined && spec.failBody !== undefined;
  const vars = ntVars(n.tpl, p);
  // card_withdrawn 到账 = 转出额 − 手续费（USDT，按 net 键）；SP5 T26 前的旧通知无 net 键时回落 amount，避免露出占位符
  if (n.tpl === 'card_withdrawn' && vars.net === undefined) vars.net = vars.amount;
  if (n.tpl === 'card_open_failed') vars.reason = str(t, REASON_KEY[String(p.reason_key ?? '')] ?? 'rsInternal');
  let bodyKey = failed && spec.failBody ? spec.failBody : spec.body;
  if (n.tpl === 'withdrawal_refunded' && positive(typeof p.paid_amount === 'string' ? p.paid_amount : undefined)) bodyKey = 'ntWdRefundPartBody';
  // 代理汇率差（bizflow F-1）：按汇率入账额与到账额不同时补「按汇率入账」（与 Bot P1 / 邮件 M-09 同措辞，用户拍板 2026-10-15），与 Bot P1 / 邮件 M-09 同一判定；旧通知无 credited_gross 不补。
  if (n.tpl === 'deposit_credited' && fxAdjusted(p.amount, p.credited_gross)) bodyKey = 'ntDepositFxBody';
  // P13 卡交易推送：商户为空时不出「（）」；入账手续费仅 > 0 时补一句（与 Bot P13-b 同判定）
  if (CARD_TX_TPL.has(n.tpl)) vars.merchant_part = typeof p.merchant === 'string' && p.merchant ? fill(str(t, 'ntTxMerchantPart'), vars) : '';
  let body = fill(str(t, bodyKey), vars);
  if (n.tpl === 'card_tx_settled' && positive(typeof p.fee === 'string' ? p.fee : undefined)) body += fill(str(t, 'ntTxFeeSuffix'), vars);
  // 激活服务费在核验通过时退回（bizflow F-2），与 Bot P7-a / 邮件 M-12 同一判定（> 0 才说明）。
  if (n.tpl === 'withdrawal_paid' && positive(typeof p.activation_refund === 'string' ? p.activation_refund : undefined)) body += fill(str(t, 'ntWdActivationRefundSuffix'), vars);
  if (n.tpl === 'tx_declined' || n.tpl === 'tx_dispute') {
    if (p.status_reason) body += fill(str(t, 'ntReasonSuffix'), vars);
    if (positive(typeof p.fee === 'string' ? p.fee : undefined)) body += fill(str(t, n.tpl === 'tx_declined' ? 'ntDeclinedFeeSuffix' : 'ntDisputeFeeSuffix'), vars);
  }
  if (n.tpl === 'fee_charged' && positive(typeof p.owed === 'string' ? p.owed : undefined)) body += fill(str(t, 'ntFeeOwedSuffix'), vars);
  return { title: str(t, failed && spec.failTitle ? spec.failTitle : spec.title), body };
}

// 字符数按码点计（后端 utf8.RuneCountInString 口径），与 maxlength 的 UTF-16 计数在 BMP 内一致
function chars(v: string): number {
  return [...v].length;
}

function faqFallback(lang: 'zh' | 'en'): ReadonlyArray<readonly [string, string]> {
  // 原型 L767 内置 3 条逐字：仅 GET /faqs 请求失败（且尚无已取列表）时回落；空列表按空态显示（06 U-09）
  return lang === 'zh'
    ? [
        ['如何充值开卡?', '先在钱包充值 USDT，然后在首页点「开卡」选择卡段并填写持卡人信息，开卡费与充值手续费会从 USDT 余额扣除，1–3 分钟到账。'],
        ['如何绑定 Telegram?', '打开 @GoodPayU_bot，点击「打开小程序」，使用当前邮箱登录后会自动绑定。'],
        ['充值未到账怎么办?', '先在钱包明细确认区块确认数，若已确认仍未到账，请在消息中心提交工单并附上 TxHash。'],
      ]
    : [
        ['How to deposit and open a card?', 'Deposit USDT, then tap New card, pick a BIN and fill in the cardholder details — fees are deducted from your USDT balance.'],
        ['How to link Telegram?', 'Open @GoodPayU_bot, tap Open app and sign in with this email — linking is automatic.'],
        ['Deposit not arrived?', 'Check the confirmations in your wallet history; if confirmed but missing, open a ticket with the TxHash.'],
      ];
}

// ---------- 加载 ----------
async function loadReferral(ctx: H5Ctx): Promise<void> {
  const g = ctx.gp;
  try {
    const referral = await g.api.call<Referral>('GET /api/user/referral');
    const comms = new CursorList<CommissionRow>((page) => g.api.call<UserPage<CommissionRow>>('GET /api/user/referral/commissions', { query: { page, size: PAGE_SIZE } }));
    setM(ctx, { referral, comms });
    await comms.loadFirst();
    bump(ctx);
    if (comms.error) g.ui.toast(str(dict(ctx), 'errNetwork'), 'error');
  } catch (e) {
    g.ui.toast(g.ui.err(e), 'error');
  }
}

async function loadMoreOf<T>(ctx: H5Ctx, list: CursorList<T> | null): Promise<void> {
  if (!list || !list.hasMore || list.moreLoading) return;
  const p = list.loadMore();
  bump(ctx);
  await p;
  bump(ctx);
  if (list.error) ctx.gp.ui.toast(str(dict(ctx), 'errNetwork'), 'error');
}

async function moveCommission(ctx: H5Ctx): Promise<void> {
  const r = cur(ctx).referral;
  if (!r || !positive(r.commission_available)) return;
  if (ctx.gp.gate.guard()) return; // U-24：未开通邮箱登录不发请求，改弹引导
  // 全局 busy 互斥：防双击重复转入（U-20 busy 态）
  await ctx.gp.ui.run('comm', async () => {
    try {
      const out = await ctx.gp.totp.run((totp) => ctx.gp.api.call('POST /api/user/wallet/commission/transfer', { body: totp ? { totp } : {} }));
      if (out === null) return;
      ctx.gp.ui.toast(str(dict(ctx), 'toastCommMoved'), 'success');
      await Promise.all([loadReferral(ctx), ctx.gp.session.reloadProfile()]);
    } catch (e) {
      ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
    }
  });
}

async function loadNotifs(ctx: H5Ctx): Promise<void> {
  const notifs = new CursorList<NotificationRow>((page) => ctx.gp.api.call<UserPage<NotificationRow>>('GET /api/user/notifications', { query: { page, size: PAGE_SIZE } }));
  setM(ctx, { notifs });
  await notifs.loadFirst();
  bump(ctx);
  if (notifs.error) ctx.gp.ui.toast(str(dict(ctx), 'errNetwork'), 'error');
}

// 首屏（page=1,size=20，E-04）：会话已换或该页签列表已被更新的请求替换时丢弃；失败 toast errNetwork（06 U-12 页签切换）
async function firstPage<T>(ctx: H5Ctx, list: CursorList<T>, current: () => CursorList<T> | null): Promise<void> {
  const my = gen;
  await list.loadFirst();
  if (my !== gen || current() !== list) return;
  bump(ctx);
  if (list.error) ctx.gp.ui.toast(str(dict(ctx), 'errNetwork'), 'error');
}

function loadNotices(ctx: H5Ctx): Promise<void> {
  const notices = new CursorList<NoticeRow>((page) => ctx.gp.api.call<UserPage<NoticeRow>>('GET /api/user/notices', { query: { page, size: PAGE_SIZE } }));
  setM(ctx, { notices });
  return firstPage(ctx, notices, () => cur(ctx).notices);
}

function loadTickets(ctx: H5Ctx): Promise<void> {
  const tickets = new CursorList<TicketRow>((page) => ctx.gp.api.call<UserPage<TicketRow>>('GET /api/user/tickets', { query: { page, size: PAGE_SIZE } }));
  setM(ctx, { tickets });
  return firstPage(ctx, tickets, () => cur(ctx).tickets);
}

function loadTab(ctx: H5Ctx, tab: 0 | 1 | 2): void {
  if (tab === 0) void loadNotices(ctx);
  else if (tab === 1) void loadTickets(ctx);
  else void loadNotifs(ctx);
}

// 跳消息中心「我的工单」页签并重拉列表（06 U-14 客服页建单后、06 U-17 申诉成功后；cards 域经此进入）
export function openTicketsTab(ctx: H5Ctx): void {
  setM(ctx, { msgTab: 1 });
  if (ctx.host.state.screen === 'messages') void loadTickets(ctx);
  else ctx.gp.ui.go('messages'); // 换屏由 update 按 m.msgTab 拉取
}

function setUnread(ctx: H5Ctx, next: (n: number) => number): void {
  const sh = ctx.host.state.sh;
  if (sh.profile) ctx.host.setState({ sh: { ...sh, profile: { ...sh.profile, unread_count: Math.max(0, next(sh.profile.unread_count)) } } });
}

function openNotification(ctx: H5Ctx, n: NotificationRow): void {
  if (!n.read_at) {
    n.read_at = new Date().toISOString();
    setUnread(ctx, (c) => c - 1);
    bump(ctx);
    // 06 U-12：不阻塞跳转，失败静默
    ctx.gp.api.call('POST /api/user/notifications/read', { body: { ids: [n.id] } }).catch((e: unknown) => console.warn('[gp:me] 标记已读失败', e));
  }
  const spec = NT[n.tpl];
  if (spec) goNotification(ctx, n, spec); // 未知 tpl 不跳转（06 U-12）
}

// 跨域入口由目标域经 gp.nav 提供；未提供 / card_id 缺失时回落到屏级（卡类 → 我的卡，明细页签 → 钱包页）
function goNotification(ctx: H5Ctx, n: NotificationRow, spec: NtSpec): void {
  const go = n.params?.result === 'failed' && spec.failGo ? spec.failGo : spec.go;
  if (go === 'card') {
    const id = Number(n.params?.card_id ?? n.ref_id ?? NaN);
    if (!ctx.gp.nav.openCard(id)) ctx.gp.ui.go('myCards');
    return;
  }
  if (go === 'ledgerDeposit' || go === 'ledgerFee') {
    if (!ctx.gp.nav.openLedger(go === 'ledgerDeposit' ? 'deposit' : 'fee')) ctx.gp.ui.go('wallet');
    return;
  }
  ctx.gp.ui.go(go);
}

async function markAllRead(ctx: H5Ctx): Promise<void> {
  try {
    await ctx.gp.api.call('POST /api/user/notifications/read', { body: { all: true } });
    const now = new Date().toISOString();
    for (const n of cur(ctx).notifs?.items ?? []) if (!n.read_at) n.read_at = now;
    setUnread(ctx, () => 0);
    bump(ctx);
  } catch (e) {
    ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
  }
}

async function loadDevices(ctx: H5Ctx): Promise<void> {
  try {
    const r = await ctx.gp.api.call<{ list: Device[] }>('GET /api/user/devices');
    setM(ctx, { devices: r.list });
  } catch (e) {
    ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
  }
}

async function removeDevice(ctx: H5Ctx, id: number): Promise<void> {
  try {
    await ctx.gp.api.call('DELETE /api/user/devices/{id}', { path: { id } });
    setM(ctx, { devices: cur(ctx).devices.filter((d) => d.id !== id) });
    ctx.gp.ui.toast(str(dict(ctx), 'toastRemoved'), 'success');
  } catch (e) {
    ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
  }
}

// 06 U-23：费率说明页数据。产品列表失败 → failed（页内重试）；报价只用于汇率与充值费示例，失败 / 充值关闭时降级（汇率「—」、示例按配置本地估算），不 toast。
// 0045：并发取 GET /wallet.deposit_fee_rate（用户生效充值费率）；失败 / 缺省回落平台价 config.fees.deposit_fee_rate，不 toast
async function loadFees(ctx: H5Ctx): Promise<void> {
  const my = gen;
  if (cur(ctx).fees.loading) return;
  setM(ctx, { fees: { ...cur(ctx).fees, loading: true, failed: false } });
  const depositOn = ctx.host.state.sh.config?.switches.deposit !== false;
  const [products, quote, wallet] = await Promise.allSettled([
    ctx.gp.api.call<CardProduct[]>('GET /api/user/card-products'),
    depositOn
      ? ctx.gp.api.call<FeeQuote>('POST /api/user/wallet/deposit-quote', { body: { network: 'TRC20', amount: EXAMPLE_DEPOSIT } })
      : Promise.reject(new Error('deposit switch off')),
    ctx.gp.api.call<UserWallet>('GET /api/user/wallet'),
  ]);
  if (my !== gen) return;
  if (products.status === 'rejected') console.error('[gp:me]', 'GET /api/user/card-products', products.reason);
  if (quote.status === 'rejected' && depositOn) console.warn('[gp:me]', 'POST /api/user/wallet/deposit-quote（费率说明页降级）', quote.reason);
  if (wallet.status === 'rejected') console.warn('[gp:me]', 'GET /api/user/wallet（费率说明页充值费率回落平台价）', wallet.reason);
  const prev = cur(ctx).fees;
  setM(ctx, {
    fees: {
      products: products.status === 'fulfilled' && Array.isArray(products.value) ? products.value : prev.products,
      quote: quote.status === 'fulfilled' ? quote.value : prev.quote,
      depRate: wallet.status === 'fulfilled' ? wallet.value.deposit_fee_rate ?? null : prev.depRate,
      loading: false,
      failed: products.status === 'rejected' && prev.products === null,
    },
  });
}

// 06 U-09：GET /faqs（标题 / 正文已按用户语言选取）；失败只记日志并回落原型 3 条，空列表即空态
async function loadFaqs(ctx: H5Ctx): Promise<void> {
  const my = gen;
  try {
    const r = await ctx.gp.api.call<FaqResp>('GET /api/user/faqs');
    if (my !== gen) return;
    const list = Array.isArray(r.list) ? r.list : [];
    setM(ctx, { faqs: list.map((x) => [x.q, x.a] as const), faqFailed: false });
  } catch (e) {
    if (my !== gen) return;
    console.error('[gp:me]', 'GET /api/user/faqs', e);
    setM(ctx, { faqFailed: true });
  }
}

function docTypeOf(v: string): KycDocType {
  return v === 'id' ? 'id' : 'passport';
}

function pristine(f: KycForm): boolean {
  return !f.name && !f.idno && !f.country;
}

// U-15 数据源：GET /kyc 一次（createPoller 首跑）；pending 时由同一轮询器每 30s 重拉（E-05），其他状态拉到即停。
// 应答过代判定：会话代号与 KYC 页进入代号任一变化即丢弃（离页 / 换人后迟到的应答不写切片）
async function kycTick(ctx: H5Ctx, visit: number): Promise<void> {
  const my = gen;
  let r: KycView;
  try {
    r = await ctx.gp.api.call<KycView>('GET /api/user/kyc');
  } catch (e) {
    if (my === gen && visit === kycVisit) console.error('[gp:me]', 'GET /api/user/kyc', e);
    return;
  }
  if (my !== gen || visit !== kycVisit) return;
  const patch: Partial<MeSlice> = { kyc: r };
  // 06 U-15：rejected 预填上次 type / name / country（证件号与图片须重填）；用户已开始填写则不覆盖
  if (r.status === 'rejected' && r.latest && pristine(cur(ctx).kycForm)) {
    patch.kycForm = { type: docTypeOf(r.latest.doc_type), name: r.latest.full_name, idno: '', country: r.latest.country };
  }
  setM(ctx, patch);
  if (r.status !== 'pending') stopKycPoll();
  if ((r.status === 'pending' || r.status === 'rejected') && r.latest) loadThumbs(ctx, visit, r.latest.files);
}

function startKycPoll(ctx: H5Ctx): void {
  if (kycPoller) return;
  const visit = kycVisit;
  kycPoller = createPoller(() => kycTick(ctx, visit), KYC_POLL_MS);
  kycPoller.start();
}

function stopKycPoll(): void {
  kycPoller?.stop();
  kycPoller = null;
}

function revokeUrl(url: string): void {
  if (url && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
}

function blobUrl(b: Blob): string {
  return typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(b) : '';
}

// D27 已提交材料：GET /kyc/files/{id} 经 gp.api.blob 取图（带 Bearer，非 <img src> 直链）；同一次进入内按 id 复用，不随轮询重取
function loadThumbs(ctx: H5Ctx, visit: number, ids: readonly string[]): void {
  const my = gen;
  for (const id of ids) {
    const key = `${visit}:${id}`;
    if (!id || cur(ctx).kycThumbs[id] || thumbInflight.has(key)) continue;
    thumbInflight.add(key);
    ctx.gp.api
      .blob('GET /api/user/kyc/files/{id}', { path: { id } })
      .then(({ blob }) => {
        const url = blobUrl(blob);
        if (my !== gen || visit !== kycVisit) return revokeUrl(url);
        setM(ctx, { kycThumbs: { ...cur(ctx).kycThumbs, [id]: url } });
      })
      .catch((e: unknown) => {
        if (my === gen && visit === kycVisit) console.error('[gp:me]', 'KYC 材料取图失败', id, e);
      })
      .finally(() => thumbInflight.delete(key));
  }
}

function revokeThumbs(ctx: H5Ctx): void {
  for (const url of Object.values(cur(ctx)?.kycThumbs ?? {})) revokeUrl(url);
}

// 进入 KYC 页：新进入代号 + 轮询器首跑；离开：停轮询、作废在途、revoke 本次进入取到的材料图
function enterKyc(ctx: H5Ctx): void {
  leaveKyc(ctx);
  startKycPoll(ctx);
}

function leaveKyc(ctx: H5Ctx): void {
  kycVisit++;
  stopKycPoll();
  revokeThumbs(ctx);
  if (Object.keys(cur(ctx).kycThumbs).length > 0) setM(ctx, { kycThumbs: {} });
}

function openKycPreview(ctx: H5Ctx, src: string): void {
  setM(ctx, { kycPreview: src });
  ctx.gp.ui.openModal({ type: 'kycPreview' });
}

// ---------- U-13a 两步验证 ----------
async function startTfa(ctx: H5Ctx): Promise<void> {
  const on = !profileOf(ctx)?.totp_enabled;
  const t = dict(ctx);
  if (!on) {
    setM(ctx, { tfa: { mode: 'off', step: 2, secret: '', qr: '', code: '', err: '' } });
    ctx.gp.ui.openModal({ type: 'tfaOff', title: str(t, 'tfaTitleOff') });
    return;
  }
  // busy 互斥：重复点击不发第二次 setup（两次 setup 乱序返回会让二维码与服务端待生效秘钥不一致）
  await ctx.gp.ui.run('tfa', async () => {
    try {
      const r = await ctx.gp.api.call<TfaSetup>('POST /api/user/2fa/setup');
      setM(ctx, { tfa: { mode: 'on', step: 1, secret: r.secret, qr: await qrDataUrl(r.otpauth_uri, qrPixels(TFA_QR_SIZE)), code: '', err: '' } });
      ctx.gp.ui.openModal({ type: 'tfaOn', title: str(t, 'tfaTitleOn') });
    } catch (e) {
      ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
    }
  });
}

function patchTfa(ctx: H5Ctx, patch: Partial<TfaFlow>): void {
  const f = cur(ctx).tfa;
  if (f) setM(ctx, { tfa: { ...f, ...patch } });
}

async function submitTfa(ctx: H5Ctx): Promise<void> {
  const f = cur(ctx).tfa;
  if (!f) return;
  if (f.code.length !== CODE_LEN) return patchTfa(ctx, { err: str(dict(ctx), 'err2fa') });
  await ctx.gp.ui.run('tfa', async () => {
    try {
      await ctx.gp.api.call(f.mode === 'on' ? 'POST /api/user/2fa/enable' : 'POST /api/user/2fa/disable', { body: { code: f.code } });
      setM(ctx, { tfa: null });
      ctx.gp.ui.closeModal();
      ctx.gp.ui.toast(str(dict(ctx), 'toast2fa'), 'success');
      await ctx.gp.session.reloadProfile();
    } catch (e) {
      patchTfa(ctx, { code: '', err: ctx.gp.ui.err(e) });
    }
  });
}

// ---------- U-13 密码 ----------
function patchPwd(ctx: H5Ctx, patch: Partial<PwdForm>): void {
  setM(ctx, { pwd: { ...cur(ctx).pwd, ...patch } });
}

async function savePwd(ctx: H5Ctx): Promise<void> {
  const t = dict(ctx);
  const p = cur(ctx).pwd;
  const has = !!profileOf(ctx)?.has_password;
  if (has && (!p.old || !p.nw || !p.nw2)) return ctx.gp.ui.toast(str(t, 'errPwd'), 'error');
  if (!PWD_RE.test(p.nw) || (has && p.nw === p.old)) return ctx.gp.ui.toast(str(t, 'errPwdRule'), 'error');
  if (p.nw !== p.nw2) return ctx.gp.ui.toast(str(t, 'errNewPwd'), 'error');
  if (!has && p.code.length !== CODE_LEN) return ctx.gp.ui.toast(str(t, 'errCode'), 'error');
  await ctx.gp.ui.run('pwd', async () => {
    try {
      const out = await ctx.gp.totp.run((totp) =>
        has
          ? ctx.gp.api.call('PUT /api/user/password', { body: { old: p.old, new: p.nw, revoke_others: p.revoke, ...(totp ? { totp } : {}) } })
          : ctx.gp.api.call('POST /api/user/password/set', { body: { email_code: p.code, new_password: p.nw, ...(totp ? { totp } : {}) } }),
      );
      if (out === null) return;
      clearPwdCd();
      setM(ctx, { pwd: fresh().pwd });
      const gated = gateMissing(profileOf(ctx)) !== null;
      ctx.gp.ui.toast(str(dict(ctx), has ? 'toastPwd' : 'setPwdDone'), 'success');
      await Promise.all([ctx.gp.session.reloadProfile(), loadDevices(ctx)]);
      // U-24：受限会话设密完成且 profile 已放行 → 提示可继续资金操作
      if (gated && gateMissing(profileOf(ctx)) === null) ctx.gp.ui.toast(str(dict(ctx), 'gateDone'), 'success');
    } catch (e) {
      ctx.gp.ui.toast(e instanceof ApiError && e.code === 1001 ? str(dict(ctx), 'errOldPwd') : ctx.gp.ui.err(e), 'error');
    }
  });
}

function clearPwdCd(): void {
  if (pwdCdTimer) clearInterval(pwdCdTimer);
  pwdCdTimer = null;
}

function startPwdCd(ctx: H5Ctx): void {
  clearPwdCd();
  patchPwd(ctx, { cd: CD_SECONDS });
  pwdCdTimer = setInterval(() => {
    const n = cur(ctx).pwd.cd - 1;
    patchPwd(ctx, { cd: Math.max(0, n) });
    if (n <= 0) clearPwdCd();
  }, 1000);
}

async function sendSetPwdCode(ctx: H5Ctx): Promise<void> {
  if (cur(ctx).pwd.cd > 0) return;
  try {
    await ctx.gp.api.call('POST /api/user/password/set/send-code'); // 无业务体：发往本人已验证邮箱（S-06 / S-28）
    startPwdCd(ctx);
    ctx.gp.ui.toast(str(dict(ctx), 'toastCode'), 'success');
  } catch (e) {
    ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
  }
}

// ---------- U-13b 绑定 / 更换邮箱 ----------
function patchEmail(ctx: H5Ctx, patch: Partial<EmailFlow>): void {
  const f = cur(ctx).emailFlow;
  if (f) setM(ctx, { emailFlow: { ...f, ...patch } });
}

function clearEmailCd(): void {
  if (emailCdTimer) clearInterval(emailCdTimer);
  emailCdTimer = null;
}

function startEmailCd(ctx: H5Ctx): void {
  clearEmailCd();
  patchEmail(ctx, { cd: CD_SECONDS });
  emailCdTimer = setInterval(() => {
    const f = cur(ctx).emailFlow;
    if (!f) return clearEmailCd();
    const n = f.cd - 1;
    patchEmail(ctx, { cd: Math.max(0, n) });
    if (n <= 0) clearEmailCd();
  }, 1000);
}

function openEmailFlow(ctx: H5Ctx): void {
  clearEmailCd();
  const t = dict(ctx);
  setM(ctx, { emailFlow: { step: 1, email: '', code: '', totp: '', cd: 0, err: '' } });
  ctx.gp.ui.openModal({ type: 'email', title: str(t, profileOf(ctx)?.email ? 'secEmailChangeTitle' : 'secEmailBindTitle') });
}

async function sendEmailCode(ctx: H5Ctx, email: string): Promise<boolean> {
  try {
    await ctx.gp.api.call('POST /api/user/email/send-code', { body: { email } });
    patchEmail(ctx, { step: 2, email, err: '' });
    startEmailCd(ctx);
    ctx.gp.ui.toast(str(dict(ctx), 'toastCode'), 'success');
    return true;
  } catch (e) {
    if (gateToMerge(ctx, e, email)) return false;
    patchEmail(ctx, { err: ctx.gp.ui.err(e) });
    return false;
  }
}

async function emailNext(ctx: H5Ctx): Promise<void> {
  const f = cur(ctx).emailFlow;
  if (!f) return;
  const t = dict(ctx);
  if (f.step === 1) {
    const email = f.email.trim();
    if (!EMAIL_RE.test(email)) return patchEmail(ctx, { err: str(t, 'errEmail') });
    await ctx.gp.ui.run('email', () => sendEmailCode(ctx, email));
    return;
  }
  const need2fa = !!profileOf(ctx)?.totp_enabled;
  if (f.code.length !== CODE_LEN) return patchEmail(ctx, { err: str(t, 'errCode') });
  if (need2fa && f.totp.length !== CODE_LEN) return patchEmail(ctx, { err: str(t, 'err2fa') });
  await ctx.gp.ui.run('email', async () => {
    try {
      await ctx.gp.api.call('POST /api/user/email/confirm', { body: { email: f.email, code: f.code, ...(need2fa ? { totp: f.totp } : {}) } });
      clearEmailCd();
      setM(ctx, { emailFlow: null });
      ctx.gp.ui.closeModal();
      ctx.gp.ui.toast(str(dict(ctx), 'emailBoundOk'), 'success');
      await ctx.gp.session.reloadProfile();
      // U-24 引导第二步：邮箱已绑定、仍缺登录密码 → 进入安全设置的设置密码表单
      if (gateMissing(profileOf(ctx)) === 'password') {
        if (ctx.host.state.screen !== 'security') ctx.gp.ui.go('security');
        ctx.gp.ui.toast(str(dict(ctx), 'gateNextPwd'), 'success');
      }
    } catch (e) {
      if (gateToMerge(ctx, e, f.email)) return;
      patchEmail(ctx, { err: ctx.gp.ui.err(e), ...(e instanceof ApiError && e.code === 1006 ? { totp: '' } : {}) });
    }
  });
}

// ---------- U-24 资金动作拦截引导 ----------
function openMergeFlow(ctx: H5Ctx, email = '', err = ''): void {
  clearEmailCd();
  setM(ctx, { emailFlow: null, merge: { email, password: '', need2fa: false, totp: '', err } });
  ctx.gp.ui.openModal({ type: 'merge', title: str(dict(ctx), 'secMerge') });
}

// 受限会话（未绑邮箱）绑定的邮箱已属其他账号（1005 已被注册 / 1009 对方不可被吸收）→ 转入 U-13c 合并弹层并预填该邮箱；
// 其余会话保持原行内错误。返回 true 表示已转入合并
function gateToMerge(ctx: H5Ctx, e: unknown, email: string): boolean {
  if (!(e instanceof ApiError) || (e.code !== 1005 && e.code !== 1009)) return false;
  if (gateMissing(profileOf(ctx)) !== 'email') return false;
  openMergeFlow(ctx, email.trim(), str(dict(ctx), 'gateMergeHint'));
  return true;
}

function gatePrimary(ctx: H5Ctx, missing: GateMissing): void {
  if (missing === 'email') return openEmailFlow(ctx);
  ctx.gp.ui.closeModal();
  if (ctx.host.state.screen !== 'security') ctx.gp.ui.go('security');
}

// 首次进入（Telegram Mini App 内）弹一次引导：按 uid 记在内存，每次打开 Mini App 提示一次，不落本地存储
let gateShownFor = '';

function maybeFirstGate(ctx: H5Ctx): void {
  const s = ctx.host.state;
  const p = s.sh.profile;
  if (!p || !s.sh.miniApp || s.modal || gateShownFor === p.uid) return;
  if (s.screen === 'login' || s.screen === 'closed' || s.screen === 'sessionExpired' || s.booting) return;
  const m = gateMissing(p);
  if (!m) return;
  gateShownFor = p.uid;
  ctx.gp.gate.open(m);
}

// ---------- U-13c 绑定已有邮箱账号 ----------
function patchMerge(ctx: H5Ctx, patch: Partial<MergeFlow>): void {
  const f = cur(ctx).merge;
  if (f) setM(ctx, { merge: { ...f, ...patch } });
}

async function submitMerge(ctx: H5Ctx): Promise<void> {
  const f = cur(ctx).merge;
  const t = dict(ctx);
  if (!f) return;
  if (!EMAIL_RE.test(f.email.trim()) || !f.password) return patchMerge(ctx, { err: str(t, 'errLogin') });
  if (f.need2fa && f.totp.length !== CODE_LEN) return patchMerge(ctx, { err: str(t, 'err2fa') });
  await ctx.gp.ui.run('merge', async () => {
    try {
      const r = await ctx.gp.api.call<TokenResp>('POST /api/user/account/merge-email', { body: { email: f.email.trim(), password: f.password, ...(f.need2fa ? { totp: f.totp } : {}) } });
      setM(ctx, { merge: null });
      // 存续（邮箱）账号的新会话：Mini App 形态 token 仅内存（TD-811 / U-20）
      await ctx.gp.session.established(r.token, { persist: !ctx.host.state.sh.miniApp, toast: str(dict(ctx), 'mergedOk') });
    } catch (e) {
      if (e instanceof ApiError && e.code === 1004) return patchMerge(ctx, { need2fa: true, totp: '', err: '' });
      patchMerge(ctx, { totp: '', err: ctx.gp.ui.err(e) });
    }
  });
}

// ---------- U-13 Telegram 绑定 / 解绑 ----------
async function identityChange(ctx: H5Ctx, action: (totp: string | undefined) => Promise<unknown>): Promise<void> {
  // busy 互斥：防 TG 绑定 / 解绑双击双发
  await ctx.gp.ui.run('tg', async () => {
    try {
      const out = await ctx.gp.totp.run(action);
      if (out === null) return;
      ctx.gp.ui.toast(str(dict(ctx), 'toastSaved2'), 'success');
      await ctx.gp.session.reloadProfile();
    } catch (e) {
      ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
    }
  });
}

async function toggleTg(ctx: H5Ctx): Promise<void> {
  const p = profileOf(ctx);
  if (!p) return;
  const t = dict(ctx);
  if (p.tg) {
    // I-04：Telegram 为唯一登录方式时前端直接拦截，不发请求
    if (!p.has_password) return ctx.gp.ui.toast(str(t, 'errUnbindOnly'), 'error');
    await identityChange(ctx, (totp) => ctx.gp.api.call('POST /api/user/telegram/unbind', { body: totp ? { totp } : {} }));
    return;
  }
  const initData = getTg()?.initData ?? '';
  if (!initData) {
    ctx.gp.ui.openModal({ type: 'tgHint', title: str(t, 'secTg') });
    return;
  }
  await identityChange(ctx, (totp) =>
    ctx.gp.api.call('POST /api/user/telegram/bind', { body: { init_data: initData, ...botIdField(ctx.host.state.sh.botId), ...(totp ? { totp } : {}) } }),
  );
}

// ---------- 外链（U-10 分享 / U-14 客服 / U-13 打开 Bot） ----------
function openExternal(ctx: H5Ctx, url: string): void {
  const tg = getTg();
  if (tg?.openTelegramLink) {
    tg.openTelegramLink(url);
    return;
  }
  const w = typeof window !== 'undefined' && typeof window.open === 'function' ? window.open(url, '_blank') : null;
  if (w) w.opener = null;
  else ctx.gp.ui.copy(url); // 06 U-14：弹窗被拦截时降级复制
}

function supportHandle(cfg: PublicConfig | null): string {
  return (cfg?.support.telegram ?? '').trim().replace(/^@/, '');
}

// ---------- U-15 KYC ----------
interface FileEvent {
  target: { files?: ArrayLike<File> | null; value?: string };
}

interface SlotClick {
  target?: unknown;
  currentTarget?: unknown;
}

function objectUrl(f: File): string {
  return typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(f) : '';
}

function revokePick(p: KycPick | null | undefined): void {
  if (p?.preview && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(p.preview);
}

function kycFileOk(f: File): boolean {
  return KYC_MIME.includes(f.type) && f.size > 0 && f.size <= KYC_MAX_BYTES;
}

function pickKycFile(ctx: H5Ctx, i: number, e: FileEvent): void {
  const f = e.target.files?.[0];
  if (!f) return;
  if (!kycFileOk(f)) {
    e.target.value = '';
    return ctx.gp.ui.toast(str(dict(ctx), 'errKycFile'), 'error');
  }
  const picks = [...cur(ctx).kycPicks];
  revokePick(picks[i]);
  picks[i] = { file: f, preview: objectUrl(f) };
  setM(ctx, { kycPicks: picks });
}

// 上传位：覆盖层 input 承接点击；浏览器把按钮内点击重定向到按钮本身时，由此转发给内部 input
function tapSlot(e: SlotClick): void {
  if (typeof Element === 'undefined') return;
  if (e.target instanceof Element && e.target.tagName === 'INPUT') return;
  if (e.currentTarget instanceof Element) {
    const input = e.currentTarget.querySelector('input[type="file"]');
    if (input instanceof HTMLElement) input.click();
  }
}

// 06 U-15 失败分支：413（后端 kycMaxBody / nginx）与 4220 field=面名（服务端 magic number 终验）→ errKycFile；
// 其余按全局错误表：1210 errKycState、429 / 1002（全站上传槽或每用户在途 1 个占满）errLocked、网络 errNetwork
function kycErrText(ctx: H5Ctx, e: unknown): string {
  if (e instanceof ApiError && (e.status === 413 || (e.code === 4220 && KYC_FACE_SET.has(e.data.field ?? '')))) return str(dict(ctx), 'errKycFile');
  return ctx.gp.ui.err(e);
}

function kycFormData(f: KycForm, files: readonly File[]): FormData {
  const form = new FormData();
  KYC_FACES.forEach((face, i) => {
    const file = files[i];
    if (file) form.append(face, file, file.name);
  });
  form.append('doc_type', f.type);
  form.append('full_name', f.name.trim());
  form.append('id_number', f.idno.trim());
  form.append('country', f.country.trim());
  return form;
}

async function submitKyc(ctx: H5Ctx): Promise<void> {
  const t = dict(ctx);
  const f = cur(ctx).kycForm;
  if (!f.name.trim() || !f.idno.trim()) return ctx.gp.ui.toast(str(t, 'errKyc'), 'error');
  if (!f.country.trim()) return ctx.gp.ui.toast(str(t, 'errKycCountry'), 'error');
  const files = cur(ctx).kycPicks.flatMap((p) => (p ? [p.file] : []));
  if (files.length !== KYC_FACES.length) return ctx.gp.ui.toast(str(t, 'errKycFiles'), 'error');
  if (!files.every(kycFileOk)) return ctx.gp.ui.toast(str(t, 'errKycFile'), 'error');
  const form = kycFormData(f, files);
  const my = gen;
  await ctx.gp.ui.run('kyc', async () => {
    try {
      await ctx.gp.api.call<{ status: string; created_at: string }>('POST /api/user/kyc', { form });
      if (my !== gen) return;
      for (const p of cur(ctx).kycPicks) revokePick(p);
      const prev = cur(ctx).kyc;
      setM(ctx, { kyc: prev ? { ...prev, status: 'pending', review_note: null } : null, kycForm: fresh().kycForm, kycPicks: [null, null, null] });
      const sh = ctx.host.state.sh;
      if (sh.profile) ctx.host.setState({ sh: { ...sh, profile: { ...sh.profile, kyc_status: 'pending' } } });
      ctx.gp.ui.toast(str(dict(ctx), 'toastKyc'), 'success');
      ctx.gp.ui.go('me');
      ctx.gp.session.reloadProfile().catch((e: unknown) => console.warn('[gp:me] profile 刷新失败', e));
    } catch (e) {
      if (my === gen) ctx.gp.ui.toast(kycErrText(ctx, e), 'error');
    }
  });
}

// ---------- U-12 工单 / U-18 工单详情 ----------
function ticketOpen(status: string): boolean {
  return status === 'open';
}

// 06 U-12：open → stOpen 黄章、resolved → stResolved 灰章；未映射值灰章 + 原值（X-34）
function ticketStatusText(t: Dict, status: string): string {
  return status === 'open' ? str(t, 'stOpen') : status === 'resolved' ? str(t, 'stResolved') : status;
}

function ticketStatusStyle(status: string): string {
  // 原型 L662 statusStyle 逐字
  const on = ticketOpen(status);
  return `font-size:11px;border-radius:999px;padding:3px 9px;font-weight:700;background:${on ? '#fff3df' : '#eef3f0'};color:${on ? '#c47a0c' : '#6b7772'}`;
}

function senderText(t: Dict, sender: string): string {
  return sender === 'user' ? str(t, 'h5wSenderUser') : sender === 'admin' ? str(t, 'h5wSenderAdmin') : sender;
}

// U-18 工单变体 rows：原型 L661 前三行（ID / 状态 / 时间）+ 完整消息线程（审计 B-23；k = 发送方 · 时间，v = 正文）
function ticketRows(t: Dict, d: TicketDetail): Array<readonly [string, string]> {
  const thread = Array.isArray(d.thread) ? d.thread : [];
  return [
    ['ID', d.biz_no],
    [str(t, 'status'), ticketStatusText(t, d.status)],
    [str(t, 'time'), fmtDateTime(d.created_at, { seconds: true })],
    ...thread.map((m) => [`${senderText(t, m.sender)} · ${fmtDateTime(m.created_at, { seconds: true })}`, m.body] as const),
  ];
}

function showTicket(ctx: H5Ctx, d: TicketDetail): void {
  setM(ctx, { ticketDetail: d });
  ctx.gp.ui.openModal({ type: 'ticket', title: str(dict(ctx), 'ticketDetail'), rows: ticketRows(dict(ctx), d) });
}

async function openTicket(ctx: H5Ctx, id: number): Promise<void> {
  const my = gen;
  try {
    const d = await ctx.gp.api.call<TicketDetail>('GET /api/user/tickets/{id}', { path: { id } });
    if (my !== gen) return;
    setM(ctx, { replyText: '' });
    showTicket(ctx, d);
  } catch (e) {
    if (my === gen) ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
  }
}

// U-18 追加回复：1–2000 字符（ticketFill）；busy 互斥；成功后整单重拉 GET /tickets/{id} 重渲染线程（不做乐观更新）
async function submitReply(ctx: H5Ctx): Promise<void> {
  const d = cur(ctx).ticketDetail;
  if (!d) return;
  const content = cur(ctx).replyText.trim();
  if (!content || chars(content) > TICKET_CONTENT_MAX) return ctx.gp.ui.toast(str(dict(ctx), 'ticketFill'), 'error');
  const my = gen;
  const id = d.id;
  const still = (): boolean => my === gen && ctx.host.state.modal?.type === 'ticket' && cur(ctx).ticketDetail?.id === id;
  await ctx.gp.ui.run('reply', async () => {
    try {
      await ctx.gp.api.call('POST /api/user/tickets/{id}/reply', { path: { id }, body: { content } });
    } catch (e) {
      if (my === gen) ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
      return;
    }
    if (my !== gen) return;
    ctx.gp.ui.toast(str(dict(ctx), 'toastReply'), 'success');
    if (still()) setM(ctx, { replyText: '' });
    try {
      const fresh = await ctx.gp.api.call<TicketDetail>('GET /api/user/tickets/{id}', { path: { id } });
      if (still()) showTicket(ctx, fresh);
    } catch (e) {
      if (my !== gen) return;
      console.error('[gp:me]', 'GET /api/user/tickets/{id}', e);
      ctx.gp.ui.toast(str(dict(ctx), 'errNetwork'), 'error');
    }
  });
}

function openTicketNew(ctx: H5Ctx): void {
  setM(ctx, { ticket: { title: '', content: '' } });
  ctx.gp.ui.openModal({ type: 'ticketNew', title: str(dict(ctx), 'msgNew') });
}

// 新单置顶：已在消息中心工单页签且列表已到 → 本地插到首行；否则（客服页入口等）跳工单页签重拉（服务端 updated_at 倒序，新单即首行）
function placeNewTicket(ctx: H5Ctx, row: TicketRow): void {
  const list = cur(ctx).tickets;
  if (ctx.host.state.screen === 'messages' && cur(ctx).msgTab === 1 && list && !list.firstLoading) {
    list.items = [row, ...list.items.filter((x) => x.id !== row.id)];
    bump(ctx);
    return;
  }
  openTicketsTab(ctx);
}

async function submitTicket(ctx: H5Ctx): Promise<void> {
  const d = cur(ctx).ticket;
  const title = d.title.trim();
  const content = d.content.trim();
  if (!title || !content || chars(title) > TICKET_TITLE_MAX || chars(content) > TICKET_CONTENT_MAX) return ctx.gp.ui.toast(str(dict(ctx), 'ticketFill'), 'error');
  const my = gen;
  await ctx.gp.ui.run('ticket', async () => {
    try {
      const row = await ctx.gp.api.call<TicketRow>('POST /api/user/tickets', { body: { title, content } });
      if (my !== gen) return;
      setM(ctx, { ticket: { title: '', content: '' } });
      ctx.gp.ui.closeModal();
      ctx.gp.ui.toast(str(dict(ctx), 'toastTicket'), 'success');
      placeNewTicket(ctx, row);
    } catch (e) {
      if (my === gen) ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
    }
  });
}

// ---------- 会话复位与资源释放 ----------
function releaseAll(ctx: H5Ctx | null): void {
  clearEmailCd();
  clearPwdCd();
  kycVisit++;
  stopKycPoll();
  if (ctx) {
    for (const p of cur(ctx)?.kycPicks ?? []) revokePick(p);
    revokeThumbs(ctx);
  }
}

// session:ready / session:lost 双复位：代号递增使上一会话的在途结果全部作废（不写切片、不 toast）
function reset(ctx: H5Ctx): void {
  gen++;
  releaseAll(ctx);
  ctx.host.setState({ m: fresh() });
}

// ---------- 样式（逐字取原型行） ----------
const GREEN_BTN = (bg: string) => `flex:1;height:48px;border:0;border-radius:14px;background:${bg};color:#fff;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px`;

function kycChipStyle(k: KycState): string {
  // 原型 L643 + 06 U-09 rejected 红 [规格补全]
  const [bg, fg] = k === 'passed' ? ['#e3f6ee', '#0d9a68'] : k === 'pending' ? ['#fff3df', '#c47a0c'] : k === 'rejected' ? ['#fdecec', '#d1434b'] : ['#eef3f0', '#6b7772'];
  return `font-size:11px;border-radius:999px;padding:3px 10px;font-weight:700;background:${bg};color:${fg}`;
}

function rowStyle(active: boolean): string {
  // 原型 L685 zhRowStyle / enRowStyle
  return `height:48px;border:1px solid ${active ? '#0d9a68' : '#e6ebe8'};border-radius:12px;background:${active ? '#e3f6ee' : '#fff'};font-weight:700;cursor:pointer`;
}

function stepStyles(step: number, i: number): { style: string; dot: string } {
  // 原型 L778–779 rsSteps
  const on = step >= i + 1;
  return {
    style: `flex:1;display:flex;flex-direction:column;align-items:center;gap:6px;font-size:11px;font-weight:700;color:${on ? '#0d9a68' : '#b6bfba'}`,
    dot: `width:26px;height:26px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;background:${on ? '#0d9a68' : '#e6ebe8'};color:${on ? '#fff' : '#8a958f'}`,
  };
}

function vals(ctx: H5Ctx): Record<string, unknown> {
  const { s, t, lang } = ctx;
  const st = s.m;
  const p = s.sh.profile;
  const cfg = s.sh.config;
  const go = (screen: Screen) => () => ctx.gp.ui.go(screen);
  const kyc = kycStateOf(ctx);
  const kycText = str(t, ({ none: 'kycNone', pending: 'kycPending', passed: 'kycOk', rejected: 'kycReject' } as const)[kyc]);
  const unread = p?.unread_count ?? 0;
  const tg = p?.tg ?? null;
  const tgName = tg?.username ? '@' + tg.username.replace(/^@/, '') : '';
  const has = !!p?.has_password;
  const needEmail = !has && !(p?.email && p.email_verified);
  const r = st.referral;
  const commHas = !!r && positive(r.commission_available);
  const comm = cfg?.commission ?? {};
  const tfa = s.modal?.type === 'tfaOn' || s.modal?.type === 'tfaOff' ? st.tfa : null;
  const ef = s.modal?.type === 'email' ? st.emailFlow : null;
  const mf = s.modal?.type === 'merge' ? st.merge : null;
  const faqs = st.faqs ?? (st.faqFailed ? faqFallback(lang) : []);
  const inviteLink = r?.invite_link ?? '';
  // invite_link 为空（无可用 Bot，X-26）时退回邀请码
  const inviteText = inviteLink || r?.code || p?.invite_code || '';
  const comms = st.comms;
  const notifs = st.notifs;
  const cf = listFlags(comms, PAGE_SIZE);
  const nf = listFlags(notifs, PAGE_SIZE);
  // 0342 公告 / 工单页签只有列表与空态槽位（无骨架 / 加载更多槽，模板只读）：首屏 20 条 + 空态
  const ntf = listFlags(st.notices, PAGE_SIZE);
  const tkf = listFlags(st.tickets, PAGE_SIZE);
  const kycFileIds = kyc === 'pending' || kyc === 'rejected' ? st.kyc?.latest?.files ?? [] : [];
  const kycThumbs = kycFileIds.flatMap((id) => {
    const src = st.kycThumbs[id];
    return src ? [src] : [];
  });
  const totpLabel = str(t, 'totp');
  const out: Record<string, unknown> = {
    // 本域新增文案经 meT 下发：模板 t.* 只引用原型字典键（build split.test 双语守卫）
    meT: Object.fromEntries(ME_TEXT_KEYS.map((k) => [k, str(t, k)])),
    isMe: s.screen === 'me', isReferral: s.screen === 'referral', isKyc: s.screen === 'kyc', isMessages: s.screen === 'messages',
    isSecurity: s.screen === 'security', isSupport: s.screen === 'support', isFees: s.screen === 'fees',
    // U-09（C-20 三级兜底：email → @tg → uid）
    avatarText: (p?.email?.[0] ?? tg?.username?.replace(/^@/, '')[0] ?? 'U').toUpperCase(),
    meTitle: p?.email || tgName || p?.uid || '',
    uidText: p?.uid ?? '',
    showTgChip: !!tg,
    tgChipText: tg ? `TG ${tg.id}${tgName ? ' · ' + tgName : ''}` : '',
    kycChipText: kycText,
    kycStatusStyle: kycChipStyle(kyc),
    meMenu: [
      { icon: '♛', label: str(t, 'menuReferral'), hint: commText(p?.commission_balance), go: go('referral') },
      { icon: '⊕', label: str(t, 'menuKyc'), hint: kycText, go: go('kyc') },
      { icon: '⚿', label: str(t, 'menuSecurity'), hint: '2FA ' + str(t, p?.totp_enabled ? 'stOn2' : 'stOff2'), go: go('security') },
      { icon: '↑', label: str(t, 'menuWithdraw'), hint: 'USDT', go: () => {
        if (!ctx.gp.gate.guard()) ctx.gp.ui.go('withdrawUsdt'); // U-24：未开通邮箱登录改弹引导
      } },
      { icon: '✉', label: str(t, 'menuMsg'), hint: '', go: go('messages') },
      { icon: '⌐', label: str(t, 'menuLang'), hint: lang === 'zh' ? '简体中文' : 'English', go: () => ctx.gp.ui.openModal({ type: 'lang', title: str(t, 'langTitle') }) },
      { icon: '☎', label: str(t, 'menuSupport'), hint: '24h', go: go('support') },
      { icon: '％', label: str(t, 'menuFees'), hint: 'USDT', go: go('fees') },
    ].map((x) => ({ ...x, hasBadge: x.icon === '✉' && unread > 0, badge: x.icon === '✉' ? badgeText(unread) : '' })),
    faqList: faqs.map(([q, a], i) => ({
      q, a, open: st.faqOpen === i,
      toggle: () => setM(ctx, { faqOpen: cur(ctx).faqOpen === i ? -1 : i }),
      iconStyle: `width:22px;height:22px;border-radius:50%;border:1px solid #dfe5e2;display:inline-flex;align-items:center;justify-content:center;font-size:13px;flex:none;transform:rotate(${st.faqOpen === i ? 45 : 0}deg);transition:transform .18s;background:${st.faqOpen === i ? '#0f1a17' : '#fff'};color:${st.faqOpen === i ? '#fff' : '#0f1a17'}`,
    })),
    logout: () => {
      ctx.gp.session.logout().catch((e: unknown) => console.error('[gp:me] logout', e));
    },
    // U-10
    refCode: r?.code ?? p?.invite_code ?? '',
    l1Count: String(r?.l1_count ?? 0),
    l2Count: String(r?.l2_count ?? 0),
    commAvailText: commText(r?.commission_available),
    commTotalText: commText(r?.commission_total),
    commBtnStyle: `width:100%;height:48px;border:0;border-radius:14px;color:#fff;font-weight:700;font-size:14px;background:${commHas ? '#0d9a68' : '#c9d2ce'};cursor:${commHas ? 'pointer' : 'not-allowed'}`,
    moveCommission: () => void moveCommission(ctx),
    copyInvite: () => {
      if (inviteText) ctx.gp.ui.copy(inviteText);
    },
    shareInvite: () => {
      const tgApp = getTg();
      if (inviteLink && tgApp?.openTelegramLink) tgApp.openTelegramLink('https://t.me/share/url?url=' + encodeURIComponent(inviteLink));
      else if (inviteText) ctx.gp.ui.copy(inviteText);
    },
    commRuleOpen: commText(comm.l1_open) + ' / ' + commText(comm.l2_open),
    commRuleTop: pct(comm.l1_top) + '% / ' + pct(comm.l2_top) + '%',
    settleText: fill(str(t, 'settleDaily'), { settle_time: comm.settle_time ?? '00:00', tz: cfg?.timezone ? tzOffsetLabel(cfg.timezone) : '' }),
    commissions: (comms?.items ?? []).map((c) => ({
      lv: c.level, user: c.source, type: str(t, c.kind === 'open' ? 'openCommission' : 'rechargeCommission'), amount: '+' + commText(c.amount), time: fmtDateTime(c.created_at, { seconds: true }),
    })),
    commsSkel: cf.skel,
    commsEmpty: cf.empty,
    commsRetry: cf.retry,
    retryComms: () => void loadReferral(ctx),
    hasMoreComms: cf.more,
    commsNoMore: cf.noMore,
    loadMoreComms: () => void loadMoreOf(ctx, cur(ctx).comms),
    busyMoreComms: !!comms?.moreLoading,
    // U-12
    msgTabs: (['msgNotice', 'msgTicket', 'msgNotify'] as const).map((k, i) => ({
      label: str(t, k),
      pick: () => {
        const tab = i === 0 ? 0 : i === 1 ? 1 : 2;
        setM(ctx, { msgTab: tab });
        loadTab(ctx, tab);
      },
      style: `flex:1;height:44px;border:0;background:transparent;font-weight:800;font-size:15px;cursor:pointer;color:${st.msgTab === i ? '#0f1a17' : '#8a958f'};border-bottom:3px solid ${st.msgTab === i ? '#0d9a68' : 'transparent'}`,
      hasBadge: i === 2 && unread > 0,
      badge: i === 2 ? badgeText(unread) : '',
    })),
    msgIsNotice: st.msgTab === 0, msgIsTicket: st.msgTab === 1, msgIsNotify: st.msgTab === 2,
    notices: (st.notices?.items ?? []).map((n) => ({ title: n.title, body: n.body, time: fmtDateTime(n.published_at, { seconds: true }) })),
    noticesEmpty: ntf.empty,
    tickets: (st.tickets?.items ?? []).map((k) => ({
      id: k.biz_no, title: k.title, time: fmtDateTime(k.updated_at, { seconds: true }),
      reply: '—', // §6-P11：TicketRow 无最新客服回复字段，引用块显示「—」（待后端增 last_reply）
      statusText: ticketStatusText(t, k.status), statusStyle: ticketStatusStyle(k.status),
      open: () => void openTicket(ctx, k.id),
    })),
    ticketsEmpty: tkf.empty,
    newTicket: () => openTicketNew(ctx),
    replyTicket: () => void submitReply(ctx),
    notifs: (notifs?.items ?? []).map((n) => ({ ...renderNotification(t, n), unread: !n.read_at, time: fmtDateTime(n.created_at, { seconds: true }), open: () => openNotification(ctx, n) })),
    notifsSkel: nf.skel,
    notifsEmpty: nf.empty,
    notifsRetry: nf.retry,
    retryNotifs: () => void loadNotifs(ctx),
    hasMoreNotifs: nf.more,
    notifsNoMore: nf.noMore,
    loadMoreNotifs: () => void loadMoreOf(ctx, cur(ctx).notifs),
    busyMoreNotifs: !!notifs?.moreLoading,
    showMarkAll: unread > 0,
    markAllRead: () => void markAllRead(ctx),
    // U-13
    toggle2FA: () => void startTfa(ctx),
    twoFAStyle: `width:52px;height:30px;border-radius:999px;border:0;cursor:pointer;position:relative;background:${p?.totp_enabled ? '#0d9a68' : '#cfd6d2'}`,
    twoFAKnob: `position:absolute;top:3px;left:${p?.totp_enabled ? '25px' : '3px'};width:24px;height:24px;border-radius:50%;background:#fff;transition:left .18s`,
    emailRowText: p?.email || str(t, 'emailNone'),
    emailBtnText: str(t, p?.email ? 'secChange' : 'secBind'),
    openEmailFlow: () => openEmailFlow(ctx),
    tgRowText: tg ? `Telegram ID ${tg.id}${tgName ? ' · ' + tgName : ''}` : str(t, 'emailNone'),
    showTgDetected: !tg && s.sh.inTelegram && !!s.sh.tgId,
    tgBtnText: str(t, tg ? 'secUnbind' : 'secBind'),
    toggleTg: () => void toggleTg(ctx),
    showMergeRow: !!p && !p.email,
    openMerge: () => openMergeFlow(ctx),
    // U-24 引导弹层（0453g）：modal.missing 由 gp.gate.open 写入；email = 两步都未完成，password = 第一步已完成
    ...gateVals(ctx),
    pwdTitle: str(t, has ? 'secPwd' : 'secSetPwd'),
    pwdSub: str(t, has ? 'secPwdSub' : 'secSetPwdSub'),
    hasPwd: has,
    setPwdForm: !!p && !has && !needEmail,
    needEmailFirst: !!p && needEmail,
    showSavePwd: has || (!!p && !needEmail),
    pwdForm: { old: st.pwd.old, nw: st.pwd.nw, nw2: st.pwd.nw2 },
    onPwd: {
      old: (e: InputEvent) => patchPwd(ctx, { old: e.target.value }),
      nw: (e: InputEvent) => patchPwd(ctx, { nw: e.target.value }),
      nw2: (e: InputEvent) => patchPwd(ctx, { nw2: e.target.value }),
    },
    // 同 U-01 条款勾选框样式
    revokeBoxStyle: `flex:none;width:18px;height:18px;border-radius:5px;display:inline-flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;margin-top:1px;border:${st.pwd.revoke ? '0' : '1px solid #dfe5e2'};background:${st.pwd.revoke ? '#0d9a68' : '#fff'};color:${st.pwd.revoke ? '#fff' : 'transparent'}`,
    toggleRevoke: () => patchPwd(ctx, { revoke: !cur(ctx).pwd.revoke }),
    setPwdCodeHint: `${str(t, 'sendCodeTo')} ${maskTarget(p?.email ?? '')}`,
    setPwdCode: st.pwd.code,
    onSetPwdCode: (e: InputEvent) => patchPwd(ctx, { code: digits(e.target.value) }),
    setPwdSendCode: () => void sendSetPwdCode(ctx),
    setPwdCdText: st.pwd.cd > 0 ? `${st.pwd.cd} ${str(t, 'resendIn')}` : str(t, 'getCode'),
    savePwd: () => void savePwd(ctx),
    busyPwd: s.busy === 'pwd',
    devices: st.devices.map((d) => ({
      name: d.name, ip: d.ip, time: fmtDateTime(d.time, { seconds: true }), label: d.current ? str(t, 'secCurrent') : '', canRemove: !d.current,
      remove: () => {
        if (!d.current) void removeDevice(ctx, d.id);
      },
    })),
    // U-13a
    tfaOpen: !!tfa,
    tfaIs1: tfa?.mode === 'on' && tfa.step === 1,
    tfaIs2: tfa?.mode === 'on' && tfa.step === 2,
    tfaSteps: [str(t, 'tfaStep1'), str(t, 'resetStep2')].map((label, i) => ({ label, n: i + 1, ...stepStyles(tfa?.step ?? 1, i) })),
    tfaQr: tfa?.qr ?? '',
    tfaSecret: (tfa?.secret ?? '').replace(/(.{4})/g, '$1 ').trim(),
    copyTfaSecret: () => ctx.gp.ui.copy(cur(ctx).tfa?.secret ?? ''),
    tfaCode: tfa?.code ?? '',
    onTfaCode: (e: InputEvent) => {
      const code = digits(e.target.value);
      patchTfa(ctx, { code, err: '' });
      if (code.length === CODE_LEN) void submitTfa(ctx);
    },
    tfaErr: tfa?.err ?? '',
    tfaPrev: () => {
      const f = cur(ctx).tfa;
      if (f?.mode === 'on' && f.step === 2) return patchTfa(ctx, { step: 1, code: '', err: '' });
      ctx.gp.ui.closeModal();
    },
    tfaPrevText: str(t, tfa?.mode === 'on' && tfa.step === 2 ? 'prev' : 'cancel'),
    tfaNext: () => {
      const f = cur(ctx).tfa;
      if (f?.mode === 'on' && f.step === 1) return patchTfa(ctx, { step: 2, err: '' });
      void submitTfa(ctx);
    },
    tfaNextText: str(t, tfa?.mode === 'off' ? 'confirm' : 'next'),
    tfaNextStyle: GREEN_BTN(tfa?.mode === 'off' ? '#d1434b' : '#0d9a68'),
    busyTfa: s.busy === 'tfa',
    // U-13b
    emailStep1: ef?.step === 1, emailStep2: ef?.step === 2,
    emailInput: ef?.email ?? '',
    onEmailInput: (e: InputEvent) => patchEmail(ctx, { email: e.target.value, err: '' }),
    emailMask: maskTarget(ef?.email ?? ''),
    emailCode: ef?.code ?? '',
    onEmailCode: (e: InputEvent) => patchEmail(ctx, { code: digits(e.target.value), err: '' }),
    showEmailTotp: !!p?.totp_enabled,
    totpLabel,
    emailTotp: ef?.totp ?? '',
    onEmailTotp: (e: InputEvent) => patchEmail(ctx, { totp: digits(e.target.value), err: '' }),
    emailErr: ef?.err ?? '',
    emailCdText: ef && ef.cd > 0 ? `${ef.cd} ${str(t, 'resendIn')}` : str(t, 'resend'),
    emailResend: () => {
      const f = cur(ctx).emailFlow;
      if (!f || f.cd > 0) return;
      void ctx.gp.ui.run('email', () => sendEmailCode(ctx, f.email));
    },
    emailPrev: () => {
      if (cur(ctx).emailFlow?.step === 2) {
        clearEmailCd();
        return patchEmail(ctx, { step: 1, code: '', totp: '', cd: 0, err: '' });
      }
      ctx.gp.ui.closeModal();
    },
    emailPrevText: str(t, ef?.step === 2 ? 'prev' : 'cancel'),
    emailNext: () => void emailNext(ctx),
    busyEmail: s.busy === 'email',
    // U-13c
    mergeEmail: mf?.email ?? '',
    onMergeEmail: (e: InputEvent) => patchMerge(ctx, { email: e.target.value, err: '' }),
    mergePwd: mf?.password ?? '',
    onMergePwd: (e: InputEvent) => patchMerge(ctx, { password: e.target.value, err: '' }),
    mergeNeed2fa: !!mf?.need2fa,
    mergeTotp: mf?.totp ?? '',
    onMergeTotp: (e: InputEvent) => patchMerge(ctx, { totp: digits(e.target.value), err: '' }),
    mergeErr: mf?.err ?? '',
    submitMergeBtn: () => void submitMerge(ctx),
    busyMerge: s.busy === 'merge',
    openBotLink: () => {
      const u = cfg?.bot?.username;
      ctx.gp.ui.closeModal();
      if (u) openExternal(ctx, `https://t.me/${u.replace(/^@/, '')}`);
    },
    // U-12 工单新建 sheet
    ticketTitle: st.ticket.title,
    onTicketTitle: (e: InputEvent) => setM(ctx, { ticket: { ...cur(ctx).ticket, title: e.target.value } }),
    ticketContent: st.ticket.content,
    onTicketContent: (e: InputEvent) => setM(ctx, { ticket: { ...cur(ctx).ticket, content: e.target.value } }),
    submitTicket: () => void submitTicket(ctx),
    busyTicket: s.busy === 'ticket',
    // U-15
    // D11：用户级累计充卡上限口径，金额取 config.limits（02 §3）
    kycTip: fill(str(t, ({ none: 'kycWhy', pending: 'kycPendingTip', passed: 'kycPassTip', rejected: 'kycRejectTip' } as const)[kyc]), {
      kycLimit: limitText(cfg?.limits.kyc_limit),
      kycLimitVerified: limitText(cfg?.limits.kyc_limit_verified),
    }),
    kycIsPending: kyc === 'pending',
    kycFormOn: kyc === 'none' || kyc === 'rejected',
    kycReject: kyc === 'rejected' && !!st.kyc?.review_note,
    kycRejectText: `${str(t, 'kycRejectReason')}：${st.kyc?.review_note ?? ''}`,
    // D27：pending / rejected 时已提交材料三缩略图（GET /kyc/files/{id} → object URL），点击全屏预览
    kycFiles: kycThumbs.map((src) => ({ src, open: () => openKycPreview(ctx, src) })),
    hasKycFiles: kycThumbs.length > 0,
    kycPreviewSrc: st.kycPreview,
    kycForm: st.kycForm,
    onKyc: {
      name: (e: InputEvent) => setM(ctx, { kycForm: { ...cur(ctx).kycForm, name: e.target.value } }),
      idno: (e: InputEvent) => setM(ctx, { kycForm: { ...cur(ctx).kycForm, idno: e.target.value } }),
      country: (e: InputEvent) => setM(ctx, { kycForm: { ...cur(ctx).kycForm, country: e.target.value } }),
    },
    kycTypes: ([['passport', 'kycPassport'], ['id', 'kycIdCard']] as const).map(([k, l]) => ({
      label: str(t, l),
      pick: () => setM(ctx, { kycForm: { ...cur(ctx).kycForm, type: k } }),
      style: `flex:1;height:42px;border:1px solid ${st.kycForm.type === k ? '#0d9a68' : '#dfe5e2'};border-radius:12px;background:${st.kycForm.type === k ? '#e3f6ee' : '#fff'};color:${st.kycForm.type === k ? '#0d9a68' : '#0f1a17'};font-weight:700;font-size:13px;cursor:pointer`,
    })),
    kycSlots: (['kycFront', 'kycBack', 'kycHold'] as const).map((l, i) => {
      const pick = st.kycPicks[i];
      return { label: str(t, l), tap: (e: SlotClick) => tapSlot(e), onFile: (e: FileEvent) => pickKycFile(ctx, i, e), preview: pick?.preview ?? '', hasPreview: !!pick?.preview };
    }),
    submitKyc: () => void submitKyc(ctx),
    busyKyc: s.busy === 'kyc',
    // U-14
    supportTg: cfg?.support.telegram ?? '',
    supportHours: cfg?.support.hours ?? '',
    openTg: () => {
      const h = supportHandle(cfg);
      if (h) openExternal(ctx, `https://t.me/${h}`);
    },
    // U-23 费率说明页：钱包 / 卡片通用 / 按可开卡产品分组；产品区加载中 / 失败（点按重试）/ 无可开卡产品三态
    feeGroups: [walletGroup(t, cfg, st.fees.quote, st.fees.depRate), cardCommonGroup(t, cfg), ...productGroups(t, st.fees.products ?? [], lang)],
    feesLoadingOn: st.fees.loading && st.fees.products === null,
    feesFailedOn: st.fees.failed && !st.fees.loading,
    feesEmptyOn: !st.fees.loading && st.fees.products !== null && st.fees.products.length === 0,
    retryFees: () => void loadFees(ctx),
    // U-19（setZh / setEn 由 shell 实现）
    zhRowStyle: rowStyle(lang === 'zh'),
    enRowStyle: rowStyle(lang === 'en'),
  };
  return out;
}

function gateVals(ctx: H5Ctx): Record<string, unknown> {
  const { s, t } = ctx;
  const missing: GateMissing = s.modal?.type === GATE_MODAL && s.modal.missing === 'password' ? 'password' : 'email';
  const stepStyle = (done: boolean, active: boolean) =>
    `display:flex;align-items:center;gap:8px;padding:10px 12px;border-radius:12px;font-size:13px;font-weight:700;background:${done ? '#e3f6ee' : '#f1f4f2'};color:${done ? '#0d9a68' : active ? '#0f1a17' : '#8a958f'}`;
  return {
    gateGuideText: str(t, missing === 'email' ? 'gateGuideEmail' : 'gateGuidePwd'),
    gateSteps: [
      { text: `${str(t, 'gateStepEmail')}${missing === 'password' ? ' ✓' : ''}`, style: stepStyle(missing === 'password', missing === 'email') },
      { text: str(t, 'gateStepPwd'), style: stepStyle(false, missing === 'password') },
    ],
    gatePrimaryText: str(t, missing === 'email' ? 'gateBindEmail' : 'gateSetPwd'),
    gatePrimary: () => gatePrimary(ctx, missing),
    // 合并入口只在未绑邮箱时出现（U-13c 前置：当前账号无邮箱）
    gateShowMerge: missing === 'email' && !s.sh.profile?.email,
    gateMerge: () => openMergeFlow(ctx),
  };
}

function refetchScreen(ctx: H5Ctx): void {
  const s = ctx.host.state;
  if (s.screen === 'me') void loadFaqs(ctx);
  else if (s.screen === 'referral') void loadReferral(ctx);
  else if (s.screen === 'messages') loadTab(ctx, s.m.msgTab);
  else if (s.screen === 'security') void loadDevices(ctx);
  else if (s.screen === 'fees') void loadFees(ctx);
  else if (s.screen === 'kyc') startKycPoll(ctx); // 轮询在跑时由 createPoller 自行回前台补跑
}

export const me: H5Domain = {
  name: 'me',
  keys: KEYS,
  modalKeys: MODAL_KEYS,
  migrated: true,
  i18n: { zh: I18N_ME.zh, en: I18N_ME.en },
  rewrites: REWRITES_ME,
  state: () => ({ m: fresh() }),
  mount(ctx) {
    ctx.events.on('session:ready', () => reset(ctx));
    ctx.events.on('session:lost', () => reset(ctx));
    // U-22 E-05：回前台立即重拉当前页查询（profile 由 shell 统一刷新；KYC pending 轮询由 createPoller 回前台补跑）
    unForeground?.();
    unForeground = ctx.gp.foreground.on(() => refetchScreen(ctx));
  },
  update(ctx, prev) {
    const s = ctx.s;
    maybeFirstGate(ctx); // U-24 首次进入（Mini App）弹一次引导
    if (prev.screen !== s.screen) {
      if (prev.screen === 'kyc') leaveKyc(ctx);
      if (s.screen === 'me') {
        void loadFaqs(ctx);
        // 06 U-09：进入本页刷新 profile（unread_count / totp_enabled 等，E-05）
        if (s.sh.profile) ctx.gp.session.reloadProfile().catch((e: unknown) => console.warn('[gp:me] profile 刷新失败', e));
      }
      if (s.screen === 'referral') void loadReferral(ctx);
      if (s.screen === 'messages') loadTab(ctx, s.m.msgTab);
      if (s.screen === 'security') void loadDevices(ctx);
      if (s.screen === 'fees') void loadFees(ctx);
      if (s.screen === 'kyc') enterKyc(ctx);
    } else if (prev.lang !== s.lang) {
      // FAQ / 公告标题正文由服务端按用户语言选取（spec §4.4）：切换语言后重拉当前页
      if (s.screen === 'me') void loadFaqs(ctx);
      else if (s.screen === 'messages' && s.m.msgTab === 0) void loadNotices(ctx);
    }
    // 弹层关闭（取消 / 蒙层 / BackButton）时释放本域流程态与倒计时
    const was = prev.modal?.type;
    if (was && was !== s.modal?.type) {
      if (was === 'email') {
        clearEmailCd();
        setM(ctx, { emailFlow: null });
      } else if (was === 'tfaOn' || was === 'tfaOff') setM(ctx, { tfa: null });
      else if (was === 'merge') setM(ctx, { merge: null });
      else if (was === 'kycPreview') setM(ctx, { kycPreview: '' });
      else if (was === 'ticket') setM(ctx, { ticketDetail: null, replyText: '' });
    }
  },
  unmount(ctx) {
    releaseAll(ctx);
    unForeground?.();
    unForeground = null;
  },
  vals,
  modal: (ctx) => {
    const t = ctx.s.modal?.type ?? '';
    return {
      isLang: t === 'lang', isTfaOn: t === 'tfaOn', isTfaOff: t === 'tfaOff', isEmail: t === 'email', isMerge: t === 'merge',
      isTicketNew: t === 'ticketNew', isKycPreview: t === 'kycPreview', isTgHint: t === 'tgHint', isEmailGate: t === GATE_MODAL,
      // dev:U-18 回复输入框（0428 工单变体）
      replyText: ctx.s.m.replyText,
      onReplyInput: (e: InputEvent) => setM(ctx, { replyText: e.target.value }),
      replyPh: str(ctx.t, 'h5wReplyPh'),
    };
  },
};

export const __test = { renderNotification, faqFallback, fresh };
